import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface Editor {
  id: number
  app_id: string
  pattern: string
  created_at: string
}

/**
 * Client mirror of server `isAppEditor`: ReplayIoAdmin (auth-me email is
 * verified-only), else no editor rows → any user, else exact / @domain match.
 */
export function isEmailAppEditor(email: string | null, editors: Editor[]): boolean {
  const lowerEmail = email?.toLowerCase() ?? null
  if (lowerEmail?.endsWith('@replay.io')) return true
  if (editors.length === 0) return true
  if (!lowerEmail) return false
  return editors.some((e) => {
    const pattern = e.pattern.toLowerCase()
    return pattern.startsWith('@') ? lowerEmail.endsWith(pattern) : lowerEmail === pattern
  })
}

interface EditorsState {
  items: Editor[]
  loading: boolean
  error: string | null
}

const initialState: EditorsState = {
  items: [],
  loading: false,
  error: null,
}

export const fetchEditors = createAsyncThunk(
  'editors/fetch',
  async (appId: string) => {
    const response = await fetch(`/api/app-editors/${appId}`)
    if (!response.ok) throw new Error('Failed to fetch editors')
    return response.json() as Promise<Editor[]>
  },
)

export const addEditor = createAsyncThunk(
  'editors/add',
  async ({ appId, pattern }: { appId: string; pattern: string }) => {
    const response = await fetch(`/api/app-editors/${appId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pattern }),
    })
    if (!response.ok) {
      const data = await response.json() as { error?: string }
      throw new Error(data.error || 'Failed to add editor')
    }
    return response.json() as Promise<Editor>
  },
)

export const removeEditor = createAsyncThunk(
  'editors/remove',
  async ({ appId, editorId }: { appId: string; editorId: number }) => {
    const response = await fetch(`/api/app-editors/${appId}/${editorId}`, {
      method: 'DELETE',
    })
    if (!response.ok) throw new Error('Failed to remove editor')
    return editorId
  },
)

const editorsSlice = createSlice({
  name: 'editors',
  initialState,
  reducers: {
    clearEditors() {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEditors.pending, (state) => {
        if (state.items.length === 0) state.loading = true
        state.error = null
      })
      .addCase(fetchEditors.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload
      })
      .addCase(fetchEditors.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch editors'
      })
      .addCase(addEditor.fulfilled, (state, action) => {
        const existing = state.items.find((e) => e.id === action.payload.id)
        if (!existing) {
          state.items.push(action.payload)
        }
      })
      .addCase(addEditor.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add editor'
      })
      .addCase(removeEditor.fulfilled, (state, action) => {
        state.items = state.items.filter((e) => e.id !== action.payload)
      })
      .addCase(removeEditor.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to remove editor'
      })
  },
})

export const { clearEditors } = editorsSlice.actions
export default editorsSlice.reducer
